import { t } from "@bezbolisno/i18n";
import { formatUAH } from "@bezbolisno/kit/money-format";
import { can } from "@bezbolisno/kit/rbac";
import { Badge } from "@bezbolisno/ui/badge";
import { Card } from "@bezbolisno/ui/card";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { z } from "zod";
import { NotFoundInShell } from "../../../components/boundaries";
import { ownSpecialistId } from "../../../lib/own-specialist";
import { AppointmentsPanel } from "../../../screens/patients/appointments-panel";
import { CompetitionsPanel } from "../../../screens/patients/competitions-panel";
import { CopyBriefButton } from "../../../screens/patients/copy-brief-button";
import { PatientCardEditor } from "../../../screens/patients/patient-card-editor";
import { workspaceQuery } from "../../../screens/patients/queries";
import { isUuid, nextApptFmt, painTone, toCardData } from "../../../screens/patients/view";
import { VisitCaptureForm } from "../../../screens/patients/visit-capture-form";
import {
  BriefPanel,
  EpisodesPanel,
  FilesPanel,
  HistoryPanel,
  MoneyPanels,
  NoEpisodeWorkspace,
  TasksPanel,
  TemplatesPanel,
} from "../../../screens/patients/workspace-panels";
import { WorkspaceTabs } from "../../../screens/patients/workspace-tabs";

const Search = z.object({
  tab: z.string().optional(),
  appointment: z.string().optional(),
});

/**
 * The patient workspace: ONE read model (`/patients/:id/workspace`); the API decides the
 * clinical partition (OQ-17) from the role — a manager gets `clinical: null`.
 */
export const Route = createFileRoute("/_authed/patients/$patientId")({
  validateSearch: Search,
  beforeLoad: ({ params }) => {
    if (!isUuid(params.patientId)) throw notFound();
  },
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(workspaceQuery(params.patientId)),
  component: PatientPage,
  notFoundComponent: NotFoundInShell,
});

function PatientPage() {
  const { patientId } = Route.useParams();
  const { appointment: appointmentParam } = Route.useSearch();
  const { session } = Route.useRouteContext();
  const role = session.role;
  const { data } = useSuspenseQuery(workspaceQuery(patientId));
  const { patient: p, todayISO, todayKyiv, operational, clinical } = data;

  const money = (
    <MoneyPanels
      patientId={patientId}
      role={role}
      operational={operational}
      todayKyiv={todayKyiv}
    />
  );

  if (!clinical) {
    const { owedMinor, giftBalanceMinor, nextBookedAt } = operational;
    return (
      <div className="fade-in">
        <div className="flex items-start gap-4 mb-5">
          <div>
            <h1 className="text-2xl font-extrabold">{p.name}</h1>
            <p className="text-[color:var(--muted)] mt-1 text-sm">
              {p.sport ?? "активність не вказана"}
            </p>
          </div>
        </div>
        <div className="metricstrip mb-5">
          <div className="ms">
            <div className="k">Статус</div>
            <div className="v">
              <Badge tone="info">{t.patient.statusLabel[p.status]}</Badge>
            </div>
          </div>
          <div className="ms">
            <div className="k">{t.appointments.nextVisit}</div>
            <div className="v">
              {nextBookedAt ? nextApptFmt.format(new Date(nextBookedAt)) : "—"}
            </div>
          </div>
          <div className="ms">
            <div className="k">{t.invoices.totalDue}</div>
            <div className={`v t-num${owedMinor > 0 ? " text-[color:var(--warn-ink)]" : ""}`}>
              {formatUAH(owedMinor)}
            </div>
          </div>
          <div className="ms">
            <div className="k">{t.gifts.title}</div>
            <div className="v t-num">
              {giftBalanceMinor > 0 ? formatUAH(giftBalanceMinor) : "—"}
            </div>
          </div>
        </div>
        <WorkspaceTabs
          tabs={[
            { id: "appointments", label: t.appointments.tab },
            { id: "invoices", label: t.invoices.forPatientTitle, badge: owedMinor > 0 },
            { id: "card", label: t.shell.cardTab },
          ]}
          panels={{
            appointments: (
              <AppointmentsPanel
                patientId={patientId}
                episodeId={null}
                appointments={operational.appointments}
                canMove={can(role, "appointment:move")}
                specialists={operational.specialists}
                lockedSpecialistId={ownSpecialistId(session)}
              />
            ),
            invoices: money,
            card: <PatientCardEditor p={toCardData(p)} />,
          }}
        />
      </div>
    );
  }

  const { brief, openTasks, nextStart, activePackage } = clinical;

  return (
    <div className="fade-in">
      <div className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-extrabold">{p.name}</h1>
          <p className="text-[color:var(--muted)] mt-1 text-sm">
            {p.sport ?? "активність не вказана"}
            {nextStart ? ` · старт: ${nextStart.title}` : ""}
          </p>
        </div>
        <div className="ml-auto">
          <CopyBriefButton
            text={clinical.briefText}
            label={t.workspace.copyBrief}
            copiedLabel={t.workspace.copied}
          />
        </div>
      </div>

      <div className="metricstrip mb-5">
        <div className="ms">
          <div className="k">Статус</div>
          <div className="v">
            <Badge tone="info">{t.patient.statusLabel[p.status]}</Badge>
          </div>
        </div>
        <div className="ms">
          <div className="k">Епізод</div>
          <div className="v">{brief.episode?.title ?? "—"}</div>
        </div>
        <div className="ms">
          <div className="k">Біль зараз</div>
          <div className="v">
            <Badge tone={painTone(brief.episode?.painNow ?? null)}>
              {brief.episode?.painNow ?? "—"}/10
            </Badge>
          </div>
        </div>
        <div className="ms">
          <div className="k">Відкриті задачі</div>
          <div className="v">{openTasks.length}</div>
        </div>
        <div className="ms">
          <div className="k">Старт</div>
          <div className="v">{nextStart ? nextStart.title : "—"}</div>
        </div>
        {activePackage ? (
          <div className="ms">
            <div className="k">{t.packages.forPatientTitle}</div>
            <div className="v">
              <Badge tone="info">
                {t.packages.visitsOf(activePackage.visitsUsed, activePackage.visitsTotal)}
              </Badge>
            </div>
          </div>
        ) : null}
      </div>

      {brief.episode ? (
        <WorkspaceTabs
          tabs={[
            { id: "today", label: t.workspace.today },
            { id: "after", label: t.workspace.after, badge: openTasks.length > 0 },
            { id: "history", label: t.workspace.history },
            { id: "episodes", label: t.shell.episodesTab },
            { id: "tasks", label: t.workspace.tasksTab, badge: openTasks.length > 0 },
            { id: "starts", label: t.workspace.startsTab },
            { id: "appointments", label: t.appointments.tab },
            { id: "invoices", label: t.invoices.forPatientTitle },
            { id: "templates", label: t.workspace.templatesTab },
            { id: "card", label: t.shell.cardTab },
            { id: "files", label: t.workspace.filesTab },
          ]}
          panels={{
            today: (
              <BriefPanel brief={brief} history={clinical.history} templates={clinical.templates} />
            ),
            after: (
              <Card className="p-5">
                <p className="text-xs text-[color:var(--muted)] mb-3">
                  {t.episodeForm.activeEpisode}: <strong>{brief.episode.title}</strong>
                </p>
                <VisitCaptureForm
                  episodeId={brief.episode.id}
                  patientId={patientId}
                  appointmentId={appointmentParam}
                  activePackage={activePackage}
                />
              </Card>
            ),
            history: (
              <HistoryPanel
                history={clinical.history}
                patientId={patientId}
                paymentStatuses={clinical.paymentStatuses}
              />
            ),
            episodes: (
              <EpisodesPanel
                activeEpisode={brief.episode}
                allEpisodes={clinical.episodes}
                patientId={patientId}
              />
            ),
            tasks: (
              <TasksPanel
                patientId={patientId}
                episodeId={brief.episode.id}
                tasks={openTasks}
                todayISO={todayISO}
                assignees={clinical.assignees}
              />
            ),
            starts: (
              <CompetitionsPanel
                patientId={patientId}
                competitions={clinical.competitions}
                todayISO={todayISO}
              />
            ),
            appointments: (
              <AppointmentsPanel
                patientId={patientId}
                episodeId={brief.episode.id}
                appointments={operational.appointments}
                canMove={can(role, "appointment:move")}
                specialists={operational.specialists}
                lockedSpecialistId={ownSpecialistId(session)}
              />
            ),
            invoices: money,
            templates: <TemplatesPanel templates={clinical.templates} />,
            card: <PatientCardEditor p={toCardData(p)} />,
            files: (
              <FilesPanel
                patientId={patientId}
                episodeId={brief.episode.id}
                files={clinical.files}
              />
            ),
          }}
        />
      ) : (
        <NoEpisodeWorkspace
          patientId={patientId}
          p={p}
          role={role}
          lockedSpecialistId={ownSpecialistId(session)}
          todayISO={todayISO}
          operational={operational}
          clinical={clinical}
        />
      )}
    </div>
  );
}
